<script setup>
import { nextTick, ref } from 'vue';
import { updateUserData } from '../../utils/api';
import StatusMessage from '../StatusMessage';
import { default as t } from '../../utils/translationStrings';

// Data.
const showMessage = ref(false);
const message = ref('');
const messageType = ref('success');
const abortButton = ref(null);
// Defined in BE.
// Todo: Pass it to FE somehow?
const passwordValidationPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#+\-_"@$!%*?&/:;,<=>[\]^`\'{|}~\.\(\)])[A-Za-z\d#+\-_"@$!%*?&/:;,<=>[\]^`\'{|}~\.\(\)]{12,}$/;

// Model value.
const modelData = ref({
  currentPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
});

// Events.
const emit = defineEmits(['abort']);

// Methods.
const submit = async () => {
  showMessage.value = true;

  // Validate whether new password matches the confirmation.
  if (modelData.value.newPassword !== modelData.value.newPasswordConfirm) {
    messageType.value = 'error';
    message.value = t.dashboardMyData.passwordMismatch;
    return;
  }

  // Validate the new password against the pattern defined by BE.
  if (!passwordValidationPattern.test(modelData.value.newPassword)) {
    messageType.value = 'error';
    message.value = t.dashboardMyData.passwordPatternFail;
    return;
  }

  // Update the password.
  const { data } = await updateUserData({
    type: 'password',
    ...modelData.value,
  });

  // If all goes well, the response will contain a new contextToken.
  // The user is logged out, which we indicate in the success message.
  if (data.shopResponse?.contextToken) {
    messageType.value = 'success';
    message.value = t.dashboardMyData.passwordUpdated;
    abortButton.value.textContent = t.dashboardMyData.closeButton;

    // Replace the current URL with login page.
    await nextTick(() => {
      window.location.replace('/login');
    });
  }
};
</script>

<template>
  <!-- Use Drupal classes for styling purposes. -->
  <form @submit.prevent="submit" class="webform-submission-form">
    <StatusMessage
      v-if="showMessage"
      v-model="showMessage"
      :message-type="messageType"
      :message-text="message" />
    <!-- Render all password inputs. -->
    <div v-for="key in Object.keys(modelData)" :key="key" class="form-item">
      <label :for="key" class="form-required">
        {{ t.dashboardMyData[key] }}
      </label>
      <input
        :id="key"
        v-model="modelData[key]"
        type="password"
        :autocomplete="key"
        required />
    </div>
    <div class="form-actions">
      <button type="submit" class="button">
        {{ t.dashboardMyData.saveButton }}
      </button>
      <button
        ref="abortButton"
        @click="emit('abort')"
        class="button button--border button--no-icon">
        {{ t.dashboardMyData.abortButton }}
      </button>
    </div>
  </form>
</template>
